import { AvatarSizes, CountryTokens, GiftIcon, type TranslationIDs, useIntl } from '@capsicum/common';
import { Accordion, Text, VerticalTimeline } from '@capsicum/common-web';
import { useWCPledgeHowItWorksTab } from '@capsicum/features/world-championship-pledge/shared';
import { type ReactNode, useMemo } from 'react';

import { ListedText } from '../../ui';

import * as S from './WCPledgeTabs.styled';

export const WCPledgeGuideTab = () => {
	const { $t } = useIntl();
	const { chilizChainDocumentationLink, formattedBonusPercentage, getFaqsWithParsed } = useWCPledgeHowItWorksTab();

	const StakeSection = useMemo(() => {
		return (
			<S.Section data-testid="wc-pledge-how-it-works-stake-section">
				<S.Subtitle variant="text1">{$t('worldChampionshipPledge.howItWorksTab.description.title')}</S.Subtitle>
				<S.SubSection>
					<div>
						<S.PointTitle variant="text1">
							{$t('worldChampionshipPledge.howItWorksTab.description.subtitle1')}
						</S.PointTitle>
						<ListedText
							points={[
								$t('worldChampionshipPledge.howItWorksTab.description.point1'),
								$t('worldChampionshipPledge.howItWorksTab.description.point2')
							]}
						/>
					</div>
					<S.ImageWrapper>
						<S.PointImage alt="Country badges" height={AvatarSizes.M} src={CountryTokens} />
					</S.ImageWrapper>
				</S.SubSection>
				<S.SubSection>
					<div>
						<S.PointTitle variant="text1">
							{$t('worldChampionshipPledge.howItWorksTab.description.subtitle2')}
						</S.PointTitle>
						<ListedText
							points={[
								$t('worldChampionshipPledge.howItWorksTab.description.point3'),
								$t('worldChampionshipPledge.howItWorksTab.description.point4')
							]}
						/>
					</div>
					<S.ImageWrapper>
						<S.PointImage alt="Chiliz Present" height={AvatarSizes.M} src={GiftIcon} />
					</S.ImageWrapper>
				</S.SubSection>
			</S.Section>
		);
	}, []);

	const GetNotifiedStep = (
		<>
			<Text variant="text3">{$t('worldChampionshipPledge.howItWorksTab.steps.getNotified.description')}</Text>
			<ListedText
				note={$t('worldChampionshipPledge.howItWorksTab.steps.getNotified.note')}
				points={[
					$t('worldChampionshipPledge.howItWorksTab.steps.getNotified.point1'),
					$t('worldChampionshipPledge.howItWorksTab.steps.getNotified.point2')
				]}
			/>
		</>
	);
	const ClaimStep = (
		<>
			<Text variant="text3">{$t('worldChampionshipPledge.howItWorksTab.steps.claimYourAssets.description')}</Text>
			<ListedText
				points={[
					$t('worldChampionshipPledge.howItWorksTab.steps.claimYourAssets.point1'),
					$t('worldChampionshipPledge.howItWorksTab.steps.claimYourAssets.point2')
				]}
			/>
		</>
	);

	const Timeline = useMemo(
		() => (
			<S.Section data-testid="wc-pledge-how-it-works-timeline-section">
				<VerticalTimeline
					items={[
						{
							text: $t('worldChampionshipPledge.howItWorksTab.steps.stakeYourChz.description'),
							title: $t('worldChampionshipPledge.howItWorksTab.steps.stakeYourChz.title')
						},
						{
							text: $t('worldChampionshipPledge.howItWorksTab.steps.earnChzWhileYouWait.description', {
								bonus: formattedBonusPercentage
							}),
							title: $t('worldChampionshipPledge.howItWorksTab.steps.earnChzWhileYouWait.title')
						},
						{
							content: GetNotifiedStep,
							title: $t('worldChampionshipPledge.howItWorksTab.steps.getNotified.title')
						},
						{
							content: ClaimStep,
							title: $t('worldChampionshipPledge.howItWorksTab.steps.claimYourAssets.title')
						}
					]}
				/>
			</S.Section>
		),
		[GetNotifiedStep, ClaimStep]
	);

	const bonus = parseInt(formattedBonusPercentage) / 100;
	const totalBonusExample = 300 * ((bonus * 3) / 12);
	const QuestionHowIsChzBonusCalculatedUk = () => (
		<>
			<S.FaqAnswer variant="text3">
				{$t('worldChampionshipPledge.howItWorksTab.faqs.howIsChzBonusCalculatedUk.content')}
			</S.FaqAnswer>
			<ListedText
				note={$t('worldChampionshipPledge.howItWorksTab.faqs.howIsChzBonusCalculatedUk.note', {
					bonus,
					bonusPercentage: formattedBonusPercentage,
					link: (chunks: ReactNode) => (
						<S.Link
							data-testid="wcPledge-howItWorks-faq-howIsChzBonusCalculatedUk-link"
							href={chilizChainDocumentationLink}
						>
							{chunks}
						</S.Link>
					),
					total: totalBonusExample.toString()
				})}
				points={[
					$t('worldChampionshipPledge.howItWorksTab.faqs.howIsChzBonusCalculatedUk.point1'),
					{
						subPoints: [
							$t('worldChampionshipPledge.howItWorksTab.faqs.howIsChzBonusCalculatedUk.point2sub1'),
							$t('worldChampionshipPledge.howItWorksTab.faqs.howIsChzBonusCalculatedUk.point2sub2')
						],
						text: $t('worldChampionshipPledge.howItWorksTab.faqs.howIsChzBonusCalculatedUk.point2')
					},
					$t('worldChampionshipPledge.howItWorksTab.faqs.howIsChzBonusCalculatedUk.point3')
				]}
				useNumericList
			/>
		</>
	);

	const wcPledgeFaqs = getFaqsWithParsed([
		{
			component: {
				content: (
					<S.FaqAnswer variant="text3">
						{$t('worldChampionshipPledge.howItWorksTab.faqs.howIsChzBonusCalculated.content', {
							link: () => (
								<S.Link
									data-testid="wc-pledge-how-it-works-faq-chiliz-chain-docs-link"
									href={chilizChainDocumentationLink}
								>
									{chilizChainDocumentationLink}
								</S.Link>
							)
						})}
					</S.FaqAnswer>
				),
				id: 'wcPledge-howItWorks-faq-howIsChzBonusCalculated',
				title: (
					<Text variant="text1">
						{$t('worldChampionshipPledge.howItWorksTab.faqs.howIsChzBonusCalculated.title')}
					</Text>
				)
			},
			key: 'howIsChzBonusCalculated'
		},
		{
			component: {
				content: <QuestionHowIsChzBonusCalculatedUk />,
				id: 'wcPledge-howItWorks-faq-howIsChzBonusCalculatedUk',
				title: (
					<Text variant="text1">
						{$t('worldChampionshipPledge.howItWorksTab.faqs.howIsChzBonusCalculatedUk.title')}
					</Text>
				)
			},
			key: 'howIsChzBonusCalculatedUk'
		}
	]);

	const formattedFaqs = wcPledgeFaqs.map(question => {
		if (typeof question !== 'string') return question;

		return {
			content: (
				<S.FaqAnswer variant="text3">
					{$t(`worldChampionshipPledge.howItWorksTab.faqs.${question}.content` as TranslationIDs)}
				</S.FaqAnswer>
			),
			id: `wcPledge-howItWorks-faq-${question}`,
			title: (
				<Text variant="text1">
					{$t(`worldChampionshipPledge.howItWorksTab.faqs.${question}.title` as TranslationIDs)}
				</Text>
			)
		};
	});

	const Faqs = useMemo(
		() => (
			<S.Section data-testid="wc-pledge-how-it-works-faqs-section">
				<Accordion items={formattedFaqs} />
			</S.Section>
		),
		[formattedFaqs]
	);

	return (
		<S.TabContentContainer>
			<S.Title variant="text9">{$t('worldChampionshipPledge.howItWorksTab.title')}</S.Title>
			{StakeSection}

			<S.Title variant="text9">{$t('worldChampionshipPledge.howItWorksTab.howToParticipate')}</S.Title>
			{Timeline}

			<S.Title variant="text9">{$t('worldChampionshipPledge.howItWorksTab.faqs.title')}</S.Title>
			{Faqs}
		</S.TabContentContainer>
	);
};
